import { Component, type ErrorInfo, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
  /** Rendered instead of the children when something throws. */
  fallback?: ReactNode;
}

interface State {
  failed: boolean;
}

/**
 * A failing WebGL context (blocked, software renderer missing, driver crash)
 * must never take the whole wire down with it. Without a boundary React
 * unmounts the root and the reader gets a blank page.
 */
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { failed: false };

  static getDerivedStateFromError(): State {
    return { failed: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[iii-news] boundary caught', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.failed) return this.props.children;

    if (this.props.fallback) return this.props.fallback;

    return (
      <div className="border border-rule bg-surface-raised p-6 text-center">
        <p className="font-serif text-lg text-ink">This part of the wire failed to load</p>
        <p className="mt-2 text-sm text-ink-muted">
          The rest of the page still works. Reloading usually clears it.
        </p>
        <button
          type="button"
          onClick={() => this.setState({ failed: false })}
          className="mt-4 border border-rule-strong px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.16em] text-ink hover:bg-ink/10"
        >
          Try again
        </button>
      </div>
    );
  }
}
